import {
  PortfolioAccountItem,
  PortfolioAccountsData,
} from 'model/modules/portfolio/accounts';
import mapCurrencyFromApi from 'rtkQuery/sharespro/mapper/shared/mapCurrencyFromApi';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import { mapOperationItemFromApi } from 'rtkQuery/sharespro/mapper/modules/operations/mapOperationsFromApi';
import mapPortfolioChartDataFromApi from './mapPortfolioChartDataFromApi';
import { PortfolioData } from 'model/modules/portfolio/portfolio';
import mapBrokerTypeFromApi from 'rtkQuery/sharespro/mapper/shared/mapBrokerTypeFromApi';
import {
  DirectionType,
  Rebalance,
  Recommendation,
} from 'model/modules/portfolio/rebalance';
import mapStrategyDealFromApi from 'rtkQuery/sharespro/mapper/modules/strategies/mapDealFromApi';

export const mapServiceItemFromApi = (
  apiService: ApiServiceItem | null | undefined
): PortfolioAccountItem['service'] => {
  if (apiService == null) {
    return {
      productId: null,
      isAllowWithdraw: false,
      allowedWallets: [],
      isStoppable: false,
      isResumable: false,
    };
  }

  return {
    productId: apiService.product_id,
    isAllowWithdraw: apiService.is_allow_withdraw,
    allowedWallets: apiService.allowed_wallets,
    isStoppable: apiService.is_stoppable,
    isResumable: apiService.is_resumable,
  };
};

export const mapPortfolioAccountItemFromApi = (
  apiAccount: ApiPortfolioAccountItem
): PortfolioAccountItem => {
  return {
    id: apiAccount.id,
    name: apiAccount.name,
    brokerType: mapBrokerTypeFromApi(apiAccount.broker),
    currency: mapCurrencyFromApi(apiAccount.currency),
    balance: apiAccount.total,
    balanceChange: apiAccount.revenue.sum,
    balanceChangePercent: apiAccount.revenue.pc,
    canWithdraw: apiAccount.can_withdraw,
    hasWithdrawRequest: apiAccount.has_withdraw_request,
    isReportable: apiAccount.is_reportable,
    usdCash: apiAccount.usd_cash,
    service: mapServiceItemFromApi(apiAccount.service),
    rebalancing:
      apiAccount.rebalancing != null
        ? mapRebalanceFromApi(apiAccount.rebalancing)
        : null,
    deals: apiAccount.deals?.map(deal => mapStrategyDealFromApi(deal, apiAccount.id)) ?? [],
    dateOpen: apiAccount.date_open != null ? new Date(apiAccount.date_open) : null,
  };
};

export const mapRebalanceFromApi = (
  apiRebalance: ApiRebalancing
): Rebalance => {
  return {
    id: apiRebalance.id,
    createdAt: new Date(apiRebalance.create_timestamp * 1000),
    recommendations: apiRebalance.recommendations.map(mapRecommendationFromApi),
  };
};

export const mapRecommendationFromApi = (
  apiRecommendation: ApiRecommendation
): Recommendation => {
  return {
    id: apiRecommendation.id,
    ticker: apiRecommendation.ticker,
    direction:
      apiRecommendation.direction === 'BUY'
        ? DirectionType.Buy
        : DirectionType.Sell,
    price: apiRecommendation.price,
    quantity: apiRecommendation.quantity,
    amount: apiRecommendation.amount,
    currency: mapCurrencyFromApi(apiRecommendation.currency),
  };
};

export const mapPortfolioAccountsDataFromApi = (
  apiData: ApiPortfolioAccountsData
): PortfolioAccountsData => {
  return {
    total: {
      amount: apiData.total.amount,
      currency: mapCurrencyFromApi(apiData.total.currency),
      balanceChange: apiData.total.revenue.sum,
      balanceChangePercent: apiData.total.revenue.pc,
    },
    items: apiData.accounts.map(mapPortfolioAccountItemFromApi),
  };
};

export const mapPortfolioPositionItemFromApi = (
  apiItem: ApiPortfolioPositionItem
): PortfolioPositionItem => {
  return {
    name: apiItem.name,
    currency: mapCurrencyFromApi(apiItem.currency),
    lockUpTill:
      apiItem.lock_up_till != null
        ? new Date(apiItem.lock_up_till * 1000)
        : null,
    buyAmount: apiItem.buy_amount,
    quantity: apiItem.quantity_precise,
    entryPrice: apiItem.price_entry,
    priceUnit: apiItem.price_unit,
    priceTotal: apiItem.price_total,
    change: apiItem.change,
    changePercent: apiItem.change_pc,
    accountId: apiItem.account_id,
    showOperations: apiItem.show_operations,
  };
};

export const mapPortfolioAnalyticsCompanyItemFromApi = (
  apiItem: ApiPortfolioAnalyticsCompanyListItem
): PortfolioAnalyticsCompanyListItem => {
  return {
    name: apiItem.ticker,
    volumePercent: apiItem.volume_percent,
  };
};

export const mapPortfolioCurrencyBalanceItemFromApi = (
  apiItem: ApiPortfolioCurrencyBalanceItem
): PortfolioCurrencyBalanceItem => {
  return {
    currency: mapCurrencyFromApi(apiItem.currency),
    amount: apiItem.balance,
  };
};

export const mapPortfolioBalanceInfo = (
  apiItem: ApiPortfolioBalanceInfo
): PortfolioBalanceInfo => {
  return {
    refill: {
      amount: apiItem.refill.sum,
      currency: mapCurrencyFromApi(apiItem.refill.currency),
    },
    withdrawal: {
      amount: apiItem.withdrawal.sum,
      currency: mapCurrencyFromApi(apiItem.withdrawal.currency),
    },
    circulation: {
      amount: apiItem.circulation.sum,
      currency: mapCurrencyFromApi(apiItem.circulation.currency),
    },
    income: {
      amount: apiItem.income.sum,
      currency: mapCurrencyFromApi(apiItem.income.currency),
    },
  };
};

const mapPortfolioDataFromApi = (
  resp: SharesproApiResponse<ApiFetchPortfolioResponseData>
): PortfolioData => {
  return {
    accounts: mapPortfolioAccountsDataFromApi(resp.data.accounts),
    positions: resp.data.positions.map(mapPortfolioPositionItemFromApi),
    analytics: resp.data.analytics.map(mapPortfolioAnalyticsCompanyItemFromApi),
    operations: resp.data.operations.map(mapOperationItemFromApi),
    chart: mapPortfolioChartDataFromApi(resp.data.chart),
    balanceInfo: mapPortfolioBalanceInfo(resp.data.balance),
    currencyBalances: resp.data.currencies.map(
      mapPortfolioCurrencyBalanceItemFromApi
    ),
  };
};

export default mapPortfolioDataFromApi;
